import { Analytics } from '@segment/analytics-node'
import type { TrackContext } from '.'
import {
  type IntegrationOptions,
  type SegmentReserveWordProperties,
  type TrackingProperties,
  defaultIntegrationOptions,
} from '.'
import { type TrackingEvents } from './TrackingEvents'

const analytics = new Analytics({
  writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY ?? '',
})

type TrackEventServerSideWithAnonymousId = {
  anonymousId: string
}

type TrackEventServerSideWithUserId = {
  userId: string
}

type TrackEventServerSideWithBothAnonymousIdAndUserId = {
  anonymousId: string
  userId: string
}

type TrackEventServerSideProps = {
  context?: TrackContext
  eventName: TrackingEvents
  options?: IntegrationOptions
  properties?: TrackingProperties
  segmentReserveProperties?: SegmentReserveWordProperties
} & (
  | TrackEventServerSideWithAnonymousId
  | TrackEventServerSideWithUserId
  | TrackEventServerSideWithBothAnonymousIdAndUserId
)

/**
 * Server-side event tracking for outbound analytic events to Segment.
 *
 * @param options.anonymousId
 * @param options.context
 * @param options.eventName
 * @param options.options
 * @param options.properties
 * @param options.segmentReserveProperties
 */
export function trackEventServerSide({
  context,
  eventName,
  options,
  properties,
  segmentReserveProperties,
  ...anonymousIdOrUserId
}: TrackEventServerSideProps) {
  const allOptions = { ...defaultIntegrationOptions, ...options }
  const allProperties = { ...properties, ...segmentReserveProperties }

  const props = {
    context,
    event: eventName,
    ...allOptions,
    properties: allProperties,
  }

  if ('userId' in anonymousIdOrUserId) {
    analytics.track({ ...props, userId: anonymousIdOrUserId.userId })
  } else if ('anonymousId' in anonymousIdOrUserId) {
    analytics.track({ ...props, anonymousId: anonymousIdOrUserId.anonymousId })
  } else {
    throw new Error(
      'trackEventServerSide requires either anonymousId or userId',
    )
  }
}
